"use client";

import { useState, useEffect } from "react";
import {
  Mail,
  Smartphone,
  Key,
  ArrowRight,
  Loader2,
  RefreshCw,
  Shield,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface TwoFactorStepProps {
  sessionToken: string;
  methods: string[];
  emailHint: string;
  preferredMethod: string;
  expiresIn: number;
  onVerify: (code: string, method: string, trustDevice: boolean) => Promise<void>;
  onResendOTP: () => Promise<{ resends_remaining: number }>;
  onCancel: () => void;
  error?: string;
}

const methodIcons: Record<string, typeof Mail> = {
  email_otp: Mail,
  totp: Smartphone,
  backup_code: Key,
};

const methodLabels: Record<string, string> = {
  email_otp: "بريد إلكتروني",
  totp: "تطبيق المصادقة",
  backup_code: "رمز استرداد",
};

const methodDescriptions: Record<string, string> = {
  email_otp: "أدخل الرمز المرسل إلى بريدك الإلكتروني",
  totp: "أدخل الرمز من تطبيق المصادقة",
  backup_code: "أدخل أحد رموز الاسترداد",
};

export function TwoFactorStep({
  sessionToken,
  methods,
  emailHint,
  preferredMethod,
  expiresIn,
  onVerify,
  onResendOTP,
  onCancel,
  error,
}: TwoFactorStepProps) {
  const [activeMethod, setActiveMethod] = useState(
    methods.includes(preferredMethod) ? preferredMethod : methods[0]
  );
  const [code, setCode] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendsRemaining, setResendsRemaining] = useState(3);
  const [countdown, setCountdown] = useState(0);
  const [sessionExpiry, setSessionExpiry] = useState(expiresIn);

  // Countdown for resend cooldown
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Session expiry countdown
  useEffect(() => {
    if (sessionExpiry > 0) {
      const timer = setTimeout(() => setSessionExpiry(sessionExpiry - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      // Session expired, go back to login
      onCancel();
    }
  }, [sessionExpiry, onCancel]);

  const handleVerify = async () => {
    if (!code.trim()) return;

    setVerifying(true);
    try {
      await onVerify(code, activeMethod, trustDevice);
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resendsRemaining <= 0 || countdown > 0) return;

    setResending(true);
    try {
      const result = await onResendOTP();
      if (result.resends_remaining === 0 && resendsRemaining > 0) {
        // Rate limited — parent already set error, disable resend
        setResendsRemaining(0);
      } else {
        setResendsRemaining(result.resends_remaining);
        setCountdown(30); // 30 second cooldown
      }
    } finally {
      setResending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && code.trim()) {
      handleVerify();
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
          <Shield className="h-6 w-6 text-primary" />
        </div>
        <CardTitle>التحقق بخطوتين</CardTitle>
        <CardDescription>
          أدخل رمز التحقق لإكمال تسجيل الدخول
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Session expiry warning */}
        {sessionExpiry < 60 && (
          <Alert variant="destructive">
            <AlertDescription>
              ستنتهي صلاحية الجلسة خلال {formatTime(sessionExpiry)}
            </AlertDescription>
          </Alert>
        )}

        {/* Error message */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Method tabs */}
        {methods.length > 1 ? (
          <Tabs value={activeMethod} onValueChange={setActiveMethod}>
            <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${methods.length}, 1fr)` }}>
              {methods.map((method) => {
                const Icon = methodIcons[method] || Shield;
                return (
                  <TabsTrigger key={method} value={method} className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    <span className="hidden sm:inline">{methodLabels[method]}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
            {methods.map((method) => (
              <TabsContent key={method} value={method} className="space-y-4">
                <p className="text-sm text-muted-foreground text-center">
                  {method === "email_otp"
                    ? `${methodDescriptions[method]} (${emailHint})`
                    : methodDescriptions[method]}
                </p>
              </TabsContent>
            ))}
          </Tabs>
        ) : (
          <p className="text-sm text-muted-foreground text-center">
            {activeMethod === "email_otp"
              ? `${methodDescriptions[activeMethod]} (${emailHint})`
              : methodDescriptions[activeMethod]}
          </p>
        )}

        {/* Code input */}
        <div className="space-y-2">
          <Label htmlFor="code">رمز التحقق</Label>
          <Input
            id="code"
            type="text"
            inputMode={activeMethod === "backup_code" ? "text" : "numeric"}
            pattern={activeMethod === "backup_code" ? undefined : "[0-9]*"}
            maxLength={activeMethod === "backup_code" ? 10 : 6}
            placeholder={activeMethod === "backup_code" ? "XXXX-XXXX" : "000000"}
            value={code}
            onChange={(e) => {
              if (activeMethod === "backup_code") {
                setCode(e.target.value.toUpperCase());
              } else {
                setCode(e.target.value.replace(/\D/g, ""));
              }
            }}
            onKeyDown={handleKeyDown}
            className="text-center text-2xl tracking-widest font-mono"
            autoFocus
          />
        </div>

        {/* Resend OTP (only for email_otp) */}
        {activeMethod === "email_otp" && (
          <div className="text-center">
            <Button
              variant="link"
              size="sm"
              onClick={handleResend}
              disabled={resending || countdown > 0 || resendsRemaining <= 0}
            >
              {resending ? (
                <Loader2 className="h-4 w-4 animate-spin ml-2" />
              ) : (
                <RefreshCw className="h-4 w-4 ml-2" />
              )}
              {countdown > 0
                ? `إعادة الإرسال (${countdown})`
                : resendsRemaining <= 0
                  ? "تم استنفاد محاولات الإرسال"
                  : "إعادة إرسال الرمز"}
            </Button>
            {resendsRemaining > 0 && resendsRemaining < 3 && (
              <p className="text-xs text-muted-foreground mt-1">
                {resendsRemaining} محاولات إعادة إرسال متبقية
              </p>
            )}
          </div>
        )}

        {/* Trust device checkbox */}
        <div className="flex items-center gap-2">
          <Checkbox
            id="trust"
            checked={trustDevice}
            onCheckedChange={(checked) => setTrustDevice(checked as boolean)}
          />
          <Label htmlFor="trust" className="text-sm cursor-pointer">
            الوثوق بهذا الجهاز لمدة 30 يوماً
          </Label>
        </div>
      </CardContent>
      <CardFooter className="flex flex-col gap-4">
        <Button
          onClick={handleVerify}
          disabled={verifying || !code.trim()}
          className="w-full"
        >
          {verifying && <Loader2 className="h-4 w-4 animate-spin ml-2" />}
          تحقق
        </Button>
        <Button variant="ghost" onClick={onCancel} className="w-full">
          <ArrowRight className="h-4 w-4 ml-2" />
          العودة لتسجيل الدخول
        </Button>
      </CardFooter>
    </Card>
  );
}
